import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminPage.css';

const fmtPct = (v) => `${Math.round((v || 0) * 100)}%`;
const fmtNum = (v) => (v ?? 0).toLocaleString('es-ES');
const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  } catch (e) { return String(v); }
};

const AdminPage = ({ user }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [topCache, setTopCache] = useState([]);
  const [disliked, setDisliked] = useState([]);
  const [activity, setActivity] = useState([]);
  const [redundancy, setRedundancy] = useState([]);
  const [gaps, setGaps] = useState({}); // { oposición: [laguna, ...] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [ov, top, disl, act, red, gap] = await Promise.all([
          axios.get('/admin/stats/overview'),
          axios.get('/admin/cache/top?limit=30'),
          axios.get('/admin/cache/disliked?limit=30'),
          axios.get('/admin/users/activity'),
          axios.get('/admin/insights/redundancy').catch(() => ({ data: [] })),
          axios.get('/admin/insights/gaps').catch(() => ({ data: {} })),
        ]);
        setOverview(ov.data);
        setTopCache(top.data);
        setDisliked(disl.data);
        setActivity(act.data);
        setRedundancy(red.data);
        setGaps(gap.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Error cargando datos');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const deleteCache = async (qaId) => {
    if (!window.confirm('¿Borrar esta entrada del cache?')) return;
    try {
      await axios.delete(`/admin/cache/${qaId}`);
      setTopCache((prev) => prev.filter((r) => r.id !== qaId));
      setDisliked((prev) => prev.filter((r) => r.id !== qaId));
    } catch (err) {
      alert('Error al borrar: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) return <div className="page-loader">Cargando admin…</div>;
  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="admin-page">
      <header className="page-header">
        <h1 className="page-title">Panel de administración</h1>
        <p className="page-subtitle">Estadísticas, caché y actividad de usuarios</p>
      </header>

      <div className="admin-tabs">
        <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
          Resumen
        </button>
        <button className={`tab ${tab === 'top' ? 'active' : ''}`} onClick={() => setTab('top')}>
          Top caché ({topCache.length})
        </button>
        <button className={`tab ${tab === 'feedback' ? 'active' : ''}`} onClick={() => setTab('feedback')}>
          Feedback negativo ({disliked.length})
        </button>
        <button className={`tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          Usuarios ({activity.length})
        </button>
        <button className={`tab ${tab === 'upload' ? 'active' : ''}`} onClick={() => setTab('upload')}>
          Subir examen
        </button>
        <button className={`tab ${tab === 'quality' ? 'active' : ''}`} onClick={() => setTab('quality')}>
          Calidad grafo
        </button>
      </div>

      {tab === 'overview' && overview && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Usuarios</div>
            <div className="stat-value">{fmtNum(overview.users_total)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Sesiones activas</div>
            <div className="stat-value">{fmtNum(overview.active_sessions)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Mensajes totales</div>
            <div className="stat-value">{fmtNum(overview.messages_total)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Tasa cache hit</div>
            <div className="stat-value">{fmtPct(overview.cache_hit_rate)}</div>
            <div className="stat-foot">
              {fmtNum(overview.cache_hits_total)} hits sobre {fmtNum(overview.cache_size + overview.cache_hits_total)} consultas
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Tamaño caché</div>
            <div className="stat-value">{fmtNum(overview.cache_active)}</div>
            <div className="stat-foot">{fmtNum(overview.cache_disabled)} desactivadas</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Feedback</div>
            <div className="stat-value">
              👍 {fmtNum(overview.feedback_up_total)} · 👎 {fmtNum(overview.feedback_down_total)}
            </div>
          </div>
          <div className="stat-card stat-card-highlight">
            <div className="stat-label">Coste OpenAI acumulado</div>
            <div className="stat-value">${(overview.cost_usd_total || 0).toFixed(2)}</div>
            <div className="stat-foot">
              {fmtNum(overview.llm_calls_total)} llamadas LLM · {fmtNum(overview.cache_hits_total)} hits cache
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Tokens entrada</div>
            <div className="stat-value">{fmtNum(overview.tokens_in_total)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Tokens salida</div>
            <div className="stat-value">{fmtNum(overview.tokens_out_total)}</div>
          </div>
        </div>
      )}

      {tab === 'top' && (
        <div className="card admin-table-wrapper">
          {topCache.length === 0 ? (
            <p className="empty-state">Aún no hay preguntas cacheadas.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Pregunta</th>
                  <th>Hits</th>
                  <th>👍</th>
                  <th>👎</th>
                  <th>Tokens</th>
                  <th>Coste</th>
                  <th>Última</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {topCache.map((r) => (
                  <tr key={r.id} className={r.disabled ? 'disabled-row' : ''}>
                    <td className="q-cell" title={r.question}>{r.question}</td>
                    <td>{r.hits}</td>
                    <td>{r.up || 0}</td>
                    <td>{r.down || 0}</td>
                    <td className="num-cell">{(r.tokens_in || 0) + (r.tokens_out || 0)}</td>
                    <td className="num-cell">${(r.cost_usd || 0).toFixed(4)}</td>
                    <td>{fmtDate(r.last_used_at)}</td>
                    <td>
                      <button className="btn-danger-mini" onClick={() => deleteCache(r.id)}>Borrar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'feedback' && (
        <div className="card admin-table-wrapper">
          {disliked.length === 0 ? (
            <p className="empty-state">No hay respuestas con feedback negativo.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Pregunta</th>
                  <th>👎</th>
                  <th>👍</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {disliked.map((r) => (
                  <tr key={r.id} className={r.disabled ? 'disabled-row' : ''}>
                    <td className="q-cell" title={r.question}>{r.question}</td>
                    <td><strong>{r.down}</strong></td>
                    <td>{r.up || 0}</td>
                    <td>{r.disabled ? <span className="badge-warn">desactivada</span> : 'activa'}</td>
                    <td>
                      <button className="btn-danger-mini" onClick={() => deleteCache(r.id)}>Borrar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'upload' && <UploadExamForm />}

      {tab === 'quality' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="card">
            <h3 className="section-title" style={{ marginBottom: 12 }}>🔀 Redundancia (SIMILAR_TO &gt; 0.85)</h3>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
              Pares de artículos casi idénticos. Candidatos a revisar/fusionar.
            </p>
            {redundancy.length === 0 ? (
              <p className="empty-state">Sin pares duplicados altos.</p>
            ) : (
              <table className="admin-table">
                <thead><tr><th>Artículo A</th><th>Artículo B</th><th>Score</th></tr></thead>
                <tbody>
                  {redundancy.map((r, i) => (
                    <tr key={i}>
                      <td>{r.num_a} <span style={{ color: '#666' }}>· {r.law_a}</span></td>
                      <td>{r.num_b} <span style={{ color: '#666' }}>· {r.law_b}</span></td>
                      <td className="num-cell">{(r.score * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {Object.entries(gaps).map(([opo, rows]) => (
            <div className="card" key={opo}>
              <h3 className="section-title" style={{ marginBottom: 12 }}>🕳️ Lagunas de {opo}</h3>
              <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
                Artículos del temario de {opo} con PageRank alto y ninguna pregunta de examen oficial de {opo}.
                Las preguntas generadas con IA y las de la otra oposición no cuentan como cobertura.
              </p>
              {rows.length === 0 ? (
                <p className="empty-state">Todos los artículos clave tienen al menos una pregunta oficial.</p>
              ) : (
                <table className="admin-table">
                  <thead><tr><th>Artículo</th><th>Ley</th><th>PageRank</th></tr></thead>
                  <tbody>
                    {rows.map((g, i) => (
                      <tr key={i}>
                        <td>{g.number}</td>
                        <td>{g.law}</td>
                        <td className="num-cell">{g.pr.toFixed(6)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div className="card admin-table-wrapper">
          {activity.length === 0 ? (
            <p className="empty-state">Sin usuarios.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Ciudad</th>
                  <th>Oposiciones</th>
                  <th>Mensajes</th>
                  <th>Última actividad</th>
                  <th>Último acceso</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((u) => (
                  <tr key={u.email}>
                    <td>{u.email}</td>
                    <td>{u.name || '—'}</td>
                    <td>{u.city || '—'}</td>
                    <td>{(u.oppositions || []).join(', ') || '—'}</td>
                    <td>{u.messages || 0}</td>
                    <td>{fmtDate(u.last_msg)}</td>
                    <td>{fmtDate(u.last_login)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const UploadExamForm = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [oppo, setOppo] = useState('Auxilio');
  const [examType, setExamType] = useState('test');
  const [session, setSession] = useState('Normal');
  const [model, setModel] = useState('Single');
  const [label, setLabel] = useState('');
  const [examJson, setExamJson] = useState('');
  const [plantillaJson, setPlantillaJson] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });

  const onFile = async (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setter(await readFileAsText(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    let questions, answers;
    try {
      const examData = JSON.parse(examJson);
      // Soportar list o {Primer Ejercicio: {questions:[]}}
      if (Array.isArray(examData)) {
        questions = examData;
      } else if (examData?.['Primer Ejercicio']?.questions) {
        questions = examData['Primer Ejercicio'].questions;
      } else {
        throw new Error('Formato exam JSON no reconocido (esperado list o {Primer Ejercicio: {questions:[]}}).');
      }
      answers = JSON.parse(plantillaJson);
      if (typeof answers !== 'object' || Array.isArray(answers)) {
        throw new Error('Plantilla debe ser un objeto {"1":"A","2":"B"...}');
      }
    } catch (err) {
      setError('JSON inválido: ' + err.message);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post('/admin/exams/upload', {
        year: parseInt(year, 10),
        opposition_type: oppo,
        exam_type: examType,
        exam_session: session,
        model_type: model,
        label: label || null,
        questions,
        correct_answers: answers,
      });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card upload-card">
      <form onSubmit={submit} className="upload-form">
        <div className="form-row">
          <div className="form-group">
            <label>Año</label>
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Oposición</label>
            <select value={oppo} onChange={(e) => setOppo(e.target.value)}>
              <option>Auxilio</option>
              <option>Tramitación Procesal</option>
            </select>
          </div>
          <div className="form-group">
            <label>Tipo</label>
            <select value={examType} onChange={(e) => setExamType(e.target.value)}>
              <option value="test">Test</option>
              <option value="case_study">Caso Práctico</option>
            </select>
          </div>
          <div className="form-group">
            <label>Sesión</label>
            <select value={session} onChange={(e) => setSession(e.target.value)}>
              <option>Normal</option>
              <option>Incidents</option>
            </select>
          </div>
          <div className="form-group">
            <label>Modelo</label>
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              <option>Single</option>
              <option>A</option>
              <option>B</option>
            </select>
          </div>
          <div className="form-group">
            <label>Etiqueta (opc.)</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Marzo, Sept…" />
          </div>
        </div>

        <div className="form-group">
          <label>Examen (JSON con preguntas) — pega o sube archivo</label>
          <input type="file" accept=".json" onChange={(e) => onFile(e, setExamJson)} />
          <textarea
            value={examJson}
            onChange={(e) => setExamJson(e.target.value)}
            rows={8}
            placeholder='[{"number":1,"text":"...","options":{"a":"...","b":"...","c":"...","d":"..."}}, ...]'
          />
        </div>

        <div className="form-group">
          <label>Plantilla de respuestas (JSON) — pega o sube archivo</label>
          <input type="file" accept=".json" onChange={(e) => onFile(e, setPlantillaJson)} />
          <textarea
            value={plantillaJson}
            onChange={(e) => setPlantillaJson(e.target.value)}
            rows={4}
            placeholder='{"1":"A","2":"C","3":"B",...}'
          />
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {result && (
          <div className="alert alert-success">
            ✅ Examen "{result.exam_name}" cargado: {result.loaded} preguntas
          </div>
        )}

        <div>
          <button type="submit" className="btn btn-primary" disabled={submitting || !examJson || !plantillaJson}>
            {submitting ? 'Subiendo…' : 'Subir examen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPage;
